import * as THREE from 'three';
import { emis, std } from '../materials.ts';
import { M } from '../prims.ts';
import { begin, done, type BaseOpts, type Model } from './types.ts';

/*
 * El Arado (docs/sendas/paladin.md, chapter 1, act 3): a nightmare of the Burn, wide and low, that ploughs the glass. A
 * long back of black glass plates like a beetle's, set on six short splayed legs; in front, three blades of fused glass
 * and slag, curved down and forward like a plough's shares, glowing at their edges where they cut; its seams red-hot,
 * a head sunk between the shoulders with two embers for eyes. A thin smoke off the back (the boil frames). No rig: the
 * fight moves it; lowering its blades is its cast clip's tilt.
 *
 * Colors: glass #0c0b10 to #24212c, the seams and the blades' edges ember #ff4f3a to #ff8a6e; no gold.
 */

const GLASS = '#0f0d14';
const GLASS_L = '#26222e';
const EMBER = '#ff4f3a';
const EMBER_L = '#ff8a6e';

export const arado = (o?: BaseOpts): Model => {
  const c = begin(o, 0.95, 913);
  const { g, rng } = c;
  const glass = std(GLASS, { roughness: 0.22, metalness: 0.35, flatShading: true });
  const glassL = std(GLASS_L, { roughness: 0.3, metalness: 0.3, flatShading: true });
  const seam = emis('#3a0e0a', EMBER, 1.4);
  const hot = emis('#5a1810', EMBER_L, 2.0);
  // The back: overlapping plates, low and wide, longer than broad.
  const back = M(new THREE.SphereGeometry(1, 10, 6, 0, Math.PI * 2, 0, Math.PI * 0.52), glass, g);
  back.position.set(0, 0.32, -0.05);
  back.scale.set(0.78, 0.42, 1.0);
  for (let i = 0; i < 5; i++) {
    const plate = M(new THREE.SphereGeometry(1, 8, 4, 0, Math.PI * 2, 0, Math.PI * 0.5), i % 2 ? glassL : glass, g);
    plate.position.set(0, 0.36 + 0.02 * (i % 2), -0.62 + i * 0.27);
    plate.scale.set(0.72 - Math.abs(i - 2) * 0.06, 0.38 - Math.abs(i - 2) * 0.03, 0.26);
    plate.rotation.x = -0.12;
    // The red seam between this plate and the next.
    const s = M(new THREE.TorusGeometry(0.62 - Math.abs(i - 2) * 0.05, 0.018, 4, 18, Math.PI), seam, g);
    s.position.set(0, 0.36, -0.5 + i * 0.27);
    s.rotation.set(Math.PI / 2 - 0.05, 0, 0);
    s.scale.set(1, 1, 0.55);
  }
  // A spine of jagged shards along the back.
  for (let i = 0; i < 6; i++) {
    const sh = M(new THREE.ConeGeometry(0.06, 0.26 + rng() * 0.12, 4), glassL, g);
    sh.position.set((rng() - 0.5) * 0.08, 0.72, -0.55 + i * 0.2);
    sh.rotation.set(-0.35, rng() * 3, (rng() - 0.5) * 0.4);
  }
  // Six short legs, splayed out like a crab's under the plates.
  for (let i = 0; i < 3; i++)
    for (const k of [-1, 1]) {
      const leg = M(new THREE.CylinderGeometry(0.05, 0.07, 0.42, 5), glassL, g);
      leg.position.set(k * 0.72, 0.16, -0.45 + i * 0.42);
      leg.rotation.z = k * 1.05;
      const foot = M(new THREE.ConeGeometry(0.06, 0.16, 4), glass, g);
      foot.position.set(k * 0.92, 0.05, -0.45 + i * 0.42);
      foot.rotation.z = Math.PI;
    }
  // The head, sunk between the shoulders, its two ember eyes.
  const head = M(new THREE.SphereGeometry(0.26, 9, 6), glass, g);
  head.position.set(0, 0.36, 0.78);
  head.scale.set(1.25, 0.7, 0.9);
  for (const k of [-1, 1]) {
    const eye = M(new THREE.SphereGeometry(0.045, 6, 5), hot, g);
    eye.position.set(k * 0.13, 0.42, 0.98);
  }
  // The three blades: curved shares of fused glass and slag, down and forward, their cutting edges hot.
  const blades: THREE.Object3D[] = [];
  for (const x of [-0.42, 0, 0.42]) {
    const b = new THREE.Group();
    b.position.set(x, 0.28, 0.9);
    g.add(b);
    const share = M(new THREE.TorusGeometry(0.3, 0.05, 4, 10, Math.PI * 0.6), glassL, b);
    share.rotation.set(0, Math.PI / 2, Math.PI * 0.95);
    share.scale.set(1, 1, 1.6);
    const edge = M(new THREE.TorusGeometry(0.3, 0.018, 3, 10, Math.PI * 0.6), hot, b);
    edge.rotation.set(0, Math.PI / 2, Math.PI * 0.95);
    edge.position.z = 0.035;
    edge.scale.set(1, 1, 1.6);
    const tip = M(new THREE.ConeGeometry(0.05, 0.22, 4), hot, b);
    tip.position.set(0, -0.24, 0.16);
    tip.rotation.x = Math.PI * 0.75;
    blades.push(b);
  }
  // A glow of the seams on the glass under it (no light: an emissive smear).
  const pool = M(new THREE.CircleGeometry(0.85, 16), new THREE.MeshBasicMaterial({ color: EMBER, transparent: true, opacity: 0.16, depthWrite: false }), g);
  pool.rotation.x = -Math.PI / 2;
  pool.position.y = 0.02;
  const tick = (n: number): void => {
    // The blades work a little, the seams breathe.
    blades.forEach((b, i) => (b.rotation.x = Math.sin(n * 0.9 + i * 1.3) * 0.08));
    (pool.material as THREE.MeshBasicMaterial).opacity = 0.13 + 0.05 * Math.sin(n * 0.7);
  };
  return done(c, 0.95, tick);
};
